const STORAGE_KEY = 'workloop.captionSettings';
const EVENT_NAME = 'workloop:caption-settings-changed';

export const CAPTION_ACTIONS = [
  { id: 'create_task', label: 'Create task', hint: 'Opens a task and uses the rest of the sentence as the title' },
  { id: 'create_reminder', label: 'Create reminder', hint: 'Creates a reminder from the rest of the sentence' },
  { id: 'add_call_list', label: 'Add to call list', hint: 'Adds the spoken name to Cell-Them / Need to call' },
  { id: 'call_user', label: 'Call user', hint: 'Starts a WorkLoop call with the spoken teammate' },
  { id: 'create_cart', label: 'Create shopping cart', hint: 'Opens a new shopping cart' },
  { id: 'add_cart_item', label: 'Add cart item', hint: 'Adds the rest of the sentence to the open cart' },
  { id: 'create_note', label: 'Create note', hint: 'Saves the rest of the sentence as a note' },
];

export const DEFAULT_CAPTION_RULES = [
  { id: 'def-task-1', phrase: 'create me a task', action: 'create_task' },
  { id: 'def-task-2', phrase: 'create a task', action: 'create_task' },
  { id: 'def-task-3', phrase: 'open a task', action: 'create_task' },
  { id: 'def-task-4', phrase: 'make a task', action: 'create_task' },
  { id: 'def-reminder-1', phrase: 'create me a reminder', action: 'create_reminder' },
  { id: 'def-reminder-2', phrase: 'create a reminder', action: 'create_reminder' },
  { id: 'def-reminder-3', phrase: 'remind me', action: 'create_reminder' },
  { id: 'def-call-list-1', phrase: 'need to call', action: 'add_call_list' },
  { id: 'def-call-list-2', phrase: 'add to call list', action: 'add_call_list' },
  { id: 'def-call-list-3', phrase: 'remember to call', action: 'add_call_list' },
  { id: 'def-call-1', phrase: 'call user', action: 'add_call_list' },
  { id: 'def-call-2', phrase: 'call', action: 'add_call_list' },
  { id: 'def-cart-1', phrase: 'create a cart', action: 'create_cart' },
  { id: 'def-cart-2', phrase: 'open shopping cart', action: 'create_cart' },
  { id: 'def-note-1', phrase: 'create a note', action: 'create_note' },
].map((rule) => ({ ...rule, aliases: [], enabled: true, builtin: true }));

const normalizePhrase = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const normalizeCaptionAliasList = (values, phrase = '') => {
  const seen = new Set();
  const skip = normalizePhrase(phrase);
  const out = [];
  (Array.isArray(values) ? values : [values]).forEach((raw) => {
    String(raw || '').split(/[\n;,]+/).forEach((piece) => {
      const value = piece.replace(/\s+/g, ' ').trim();
      if (!value) return;
      const key = normalizePhrase(value);
      if (!key || key === skip || seen.has(key)) return;
      seen.add(key);
      out.push(value);
    });
  });
  return out;
};

export const captionRuleMatchPhrases = (rule) => {
  const aliases = normalizeCaptionAliasList(rule?.aliases, rule?.phrase);
  return [String(rule?.phrase || '').trim(), ...aliases].filter(Boolean);
};

const newRuleId = () => `cap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const sanitizeRule = (rule, { builtin = false } = {}) => {
  const phrase = String(rule?.phrase || '').replace(/\s+/g, ' ').trim();
  const action = CAPTION_ACTIONS.some((item) => item.id === rule?.action)
    ? rule.action
    : 'create_task';
  if (!phrase) return null;
  return {
    id: String(rule?.id || newRuleId()),
    phrase,
    aliases: normalizeCaptionAliasList(rule?.aliases, phrase),
    action,
    enabled: rule?.enabled !== false,
    builtin: Boolean(builtin || rule?.builtin),
  };
};

const mergeRules = (stored) => {
  const custom = (Array.isArray(stored?.rules) ? stored.rules : [])
    .map((rule) => sanitizeRule(rule))
    .filter(Boolean);
  const byId = new Map(custom.map((rule) => [rule.id, rule]));
  const defaults = DEFAULT_CAPTION_RULES.map((rule) => {
    const override = byId.get(rule.id);
    byId.delete(rule.id);
    if (!override) return { ...rule };
    return {
      ...rule,
      phrase: override.phrase || rule.phrase,
      aliases: normalizeCaptionAliasList(override.aliases, override.phrase || rule.phrase),
      action: override.action || rule.action,
      enabled: override.enabled !== false,
    };
  });
  const extras = [...byId.values()].filter((rule) => !rule.builtin);
  return [...defaults, ...extras];
};

export const loadCaptionRules = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return mergeRules(raw ? JSON.parse(raw) : {});
  } catch {
    return mergeRules({});
  }
};

export const saveCaptionRules = (rules) => {
  const next = (Array.isArray(rules) ? rules : [])
    .map((rule) => sanitizeRule(rule, { builtin: rule?.builtin }))
    .filter(Boolean);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ rules: next }));
  } catch {
    // ignore quota
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { rules: next } }));
  }
  return mergeRules({ rules: next });
};

export const subscribeCaptionRules = (listener) => {
  const onChange = () => listener(loadCaptionRules());
  window.addEventListener(EVENT_NAME, onChange);
  return () => window.removeEventListener(EVENT_NAME, onChange);
};

export const captionActionLabel = (actionId) =>
  CAPTION_ACTIONS.find((item) => item.id === actionId)?.label || actionId;

export const matchCaptionRules = (utterance, rules = loadCaptionRules()) => {
  const text = String(utterance || '').replace(/\s+/g, ' ').trim();
  const normalized = normalizePhrase(text);
  if (!normalized) return [];
  const hits = [];
  (rules || []).forEach((rule) => {
    if (!rule?.enabled) return;
    captionRuleMatchPhrases(rule).forEach((spoken) => {
      const phrase = normalizePhrase(spoken);
      if (!phrase) return;
      const token = ` ${normalized} `;
      const needle = ` ${phrase} `;
      let from = 0;
      while (from < token.length) {
        const at = token.indexOf(needle, from);
        if (at < 0) break;
        hits.push({
          rule,
          spoken,
          start: at,
          end: at + needle.length,
          length: phrase.length,
        });
        from = at + 1;
      }
    });
  });
  hits.sort((a, b) => a.start - b.start || b.length - a.length);
  const kept = [];
  let cursor = -1;
  hits.forEach((hit) => {
    if (hit.start < cursor) return;
    kept.push(hit);
    cursor = hit.end;
  });
  return kept.map((hit) => {
    const escaped = String(hit.spoken || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = escaped ? text.match(new RegExp(escaped, 'i')) : null;
    const remainder = match
      ? text.slice((match.index || 0) + match[0].length).replace(/^[\s:,\-–—]+/, '').trim()
      : '';
    return {
      action: hit.rule.action,
      remainder,
      phrase: hit.spoken,
      ruleId: hit.rule.id,
    };
  });
};

export const parseSpokenReminderWhen = (raw) => {
  const text = String(raw || '');
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const stamp = (date) => ({
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  });
  const inMatch = text.match(/\bin\s+(\d+)\s*(?:min|mins|minute|minutes)\b/i);
  if (inMatch) {
    const later = new Date(now.getTime() + Number(inMatch[1]) * 60000);
    return { ...stamp(later), title: text.replace(inMatch[0], ' ').replace(/\s+/g, ' ').trim() };
  }
  const clock = text.match(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/i);
  let when = new Date(now.getTime() + 60 * 60000);
  let title = text;
  if (clock && (clock[3] || Number(clock[1]) > 12 || clock[0].toLowerCase().includes('at'))) {
    let hour = Number(clock[1]);
    const minute = Number(clock[2] || 0);
    const ampm = String(clock[3] || '').toLowerCase();
    if (ampm === 'pm' && hour < 12) hour += 12;
    if (ampm === 'am' && hour === 12) hour = 0;
    when = new Date(now);
    when.setHours(hour, minute, 0, 0);
    if (when.getTime() <= now.getTime()) when.setDate(when.getDate() + 1);
    title = text.replace(clock[0], ' ').replace(/\s+/g, ' ').trim();
  }
  title = title
    .replace(/^(?:to|that|about|for)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { ...stamp(when), title };
};
